/**
 * Patient Chart Design Tokens
 *
 * Derived from the centralized design system tokens (src/tokens/design-tokens.ts).
 * These provide chart-specific convenience aliases while maintaining a single source of truth.
 *
 * Extracted from Figma: Medarch-DS v.0.1
 */

import { PRIMITIVE, ALIAS, COMPONENT, px } from "@/tokens/design-tokens";
import { ECARE_LETTER_SPACING } from "@/tokens/ecare-tokens";

const c = PRIMITIVE.color;

// ─── Colors ──────────────────────────────────────────────────────────────────

export const CHART_COLORS = {
  /** Primary brand / active elements / buttons */
  primary: c.brandBlue[700],
  /** Pressed / active tab text */
  primaryPressed: c.brandBlue[900],
  /** Button hover bg */
  primaryHover: c.brandBlue[1000],
  secondaryHover: c.brandBlue[25],

  /** Main text color */
  textDark: c.neutrals[2400],
  /** Secondary label text */
  textLabel: c.neutrals[2200],
  /** Tertiary text — column headers, hints, placeholders */
  textTertiary: c.neutrals[2000],
  /** Light text on dark bg */
  textLight: c.neutrals[100],
  /** surface color */
  surfaceColor: c.neutrals[700],

  /** Table/card header fill — the page canvas, per styleguide §11 */
  surfaceHeader: COMPONENT.table.bg.header,
  /** Default surface / z-4 */
  surfaceDefault: ALIAS.surface.canvas.slate.z4,
  /** Pure white surface */
  surfaceWhite: c.neutrals["000"],
  /** Alternating row bg */
  surfaceAlt: c.neutrals[400],

  /** Button - primary */
  btnPrimaryBg: "#004186",
  btnPrimaryHoverBg: "#032952",
  /** Button - secondary */
  btnSecondaryBg: c.neutrals["000"],
  btnSecondaryText: c.brandBlue[700],
  btnSecondaryHoverBg: c.brandBlue[25],
  /** Button - ghost */
  btnGhostText: c.neutrals[2500],
  btnGhostBorder: c.neutrals[900],
  btnGhostHoverBg: c.neutrals[200],

  /** Tertiary navbar / secondary action bg */
  bgSecondaryBlue: c.brandBlue[25],
  /** Tab hover/active bg */
  bgSecondaryHover: c.brandBlue[50],
  /** Patient info section bg */
  bgSecondaryYellow: c.orange["00"],
  /** Patient tag warning bg */
  bgWarning: c.orange[50],

  /** Input field background */
  inputBg: ALIAS.surface.action.input.default,

  /** Borders / separators / outlines */
  border: ALIAS.outline.barelyGrey,
  /** Hover state border */
  borderHover: c.neutrals[1700],

  /** Navbar background color */
  navbarPrimaryBg: c.brandBlue[800],
  /** Badge - success */
  badgeSuccessBg: c.green[50],
  badgeSuccessText: c.green[700],
  /** Badge - error */
  badgeErrorBg: c.red[50],
  badgeErrorText: c.red[600],
  /** Badge - warning / flag (e.g. "Diabetic") */
  badgeWarningBg: c.orange[100],
  badgeWarningText: c.orange[700],
  /** Badge - info (e.g. "Confirmed") */
  badgeInfoBg: c.brandBlue[25],
  badgeInfoText: c.brandBlue[700],
  /** Badge - confirmed (e.g. confirmed appointment) */
  badgeConfirmedBg: c.brandBlue[100],
  badgeConfirmedText: c.brandBlue[800],
  /** Badge - default */
  badgeDefaultBg: c.neutrals[1000],
  badgeDefaultText: c.neutrals[2400],
  /** Badge - neutral (e.g. "3 Allergies") */
  badgeNeutralBg: c.neutrals[2000],
  badgeNeutralText: c.neutrals[100],
  /** Badge - pending (e.g. patint chart forms pending status) */
  badgePendingBg: c.orange[50],
  badgePendingText: c.orange[600],
  /** Badge - negative (e.g. "Risk 0.28") */
  badgeNegativeBg: c.red[50],
  badgeNegativeText: c.red[600],

  /** Communication indicator dot */
  statusLive: ALIAS.semantics.success.default,

  // ─── Legacy snap-to-token (for financial / eligibility / encounter files)
  //     These replace hardcoded near-match hex values found across patient-list.
  //     Each entry snaps to the closest PRIMITIVE neutral (≤2 hex diff).
  /** Dark cell text — replaces #373D41 */
  textDarkMuted: c.neutrals[2300],
  /** Darker label/header text — replaces #21262B */
  textDarker: c.neutrals[2500],
  /** Muted label grey — replaces #74797B */
  textMutedGrey: c.neutrals[2000],
  /** Light border — replaces #E7E7E7 */
  borderLight: c.neutrals[900],
  /** Subtle header bg — replaces #F8F8F8 */
  surfaceSubtle: c.neutrals[300],
  /** Mid brand blue (500) — replaces #2771B1 */
  primaryMid: c.brandBlue[500],

  // provider config
  settingsConfigSubNavbg: c.neutrals[800],
  settingsConfigSubnavTitleColor: c.lightBlue[550],
  settingsConfigTitleBg: c.neutrals["000"],
} as const;

// ─── Typography ──────────────────────────────────────────────────────────────

export const CHART_TYPOGRAPHY = {
  fontFamily: PRIMITIVE.typography.fontFamily,

  /** Body/5 — most common text size */
  body5: {
    fontSize: { xs: px(PRIMITIVE.typography.fontSize.body.b5), lg: "12px" },
    lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][12]),
  },
  /** Body/4 — medium text */
  body4: {
    fontSize: { xs: px(PRIMITIVE.typography.fontSize.body.b4) },
    lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][14]),
  },
  /** Body/3 — patient name size */
  body3: {
    fontSize: {
      xs: px(PRIMITIVE.typography.fontSize.body.b3),
      lg: px(PRIMITIVE.typography.fontSize.body.b2),
    },
    lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][16]),
  },
  /** Title/6 — same as body5 but used for titles */
  title6: {
    fontSize: { xs: px(PRIMITIVE.typography.fontSize.title.t6), lg: "13px" },
    lineHeight: px(PRIMITIVE.typography.lineHeight["120%"][14]),
  },

  weight: {
    light: PRIMITIVE.typography.fontWeight.light,
    regular: PRIMITIVE.typography.fontWeight.regular,
    medium: PRIMITIVE.typography.fontWeight.medium,
    semiBold: PRIMITIVE.typography.fontWeight.semiBold,
  },

  /** Overline treatment — column headers, eyebrow labels (§2.2). */
  overline: {
    fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
    letterSpacing: ECARE_LETTER_SPACING.overline,
    textTransform: "uppercase",
  },
} as const;

// ─── Spacing / Dimensions ────────────────────────────────────────────────────

const s = PRIMITIVE.spacing;

export const CHART_DIMENSIONS = {
  /** DataFieldRow label column width */
  labelWidth: { xs: "107px", lg: "118px" },
  /** DataFieldRow separator width */
  separatorWidth: px(s[12]),
  /** TertiaryNavbar container height */
  tertiaryNavHeight: { xs: px(s[36]), lg: px(s[36]) },
  /** Search input width */
  searchWidth: { xs: "226px", lg: "249px" },
  /** Search input / action button height */
  controlHeight: { xs: "26px" },

  /** AccordionRecord header height */
  accordionHeaderHeight: { xs: px(s[32] + 2), lg: px(s[32] + 2) },

  /** Card header height (regular card) */
  cardHeaderHeight: { xs: "27px", lg: "30px" },
  /** Card header height (with extra content e.g. Mark as Primary) */
  cardHeaderHeightLarge: px(s[40] + s[4]),
  /** Card header padding */
  cardHeaderPx: { xs: px(s[8]), lg: "9px" },
  /** Card header padding x (large — e.g. insurance card) */
  cardHeaderPxLarge: px(s[12]),
  cardHeaderPy: { xs: "6px", lg: "7px" },
  /** Card row padding */
  cardRowPx: { xs: px(s[8]), lg: "9px" },
  cardRowPy: { xs: px(s[4]), lg: "5px" },
  /** Card row min height */
  cardRowMinHeight: { xs: "26px", lg: "29px" },

  /** Icon sizes (never scaled) */
  iconSm: px(s[16]),
  iconMd: "18px",

  /** Border radius */
  borderRadius: px(COMPONENT.card.radius),
  /** Badge border radius */
  badgeRadius: px(COMPONENT.card.radius),
  infoBadge: px(COMPONENT.modal.radius),

  // ─── PatientChartTabs ─────────────────────────────────────────────────────
  /** Main tab icon size */
  tabIconSize: px(s[24]),
  /** Main tab horizontal padding */
  tabPx: "6px",
  /** Main tab vertical padding */
  tabPy: px(s[8]),
  /** Gap between tab icon and label */
  tabGap: px(s[4]),

  /** Communication unread dot badge — positional offset and text size */
  commBadgeTop: "-11px",
  commBadgeRight: "-10px",
  commBadgeFontSize: "11px",

  /** Pinned-tab indicator (shown on visit-note page) */
  pinIndicatorSize: "22px",
  pinIndicatorOffset: "-6px",
  pinIconSize: px(s[12]),
  pinIndicatorBorder: `${px(PRIMITIVE.spacing[1])} solid ${ALIAS.outline.lightGrey}`,

  /** Patient info side-bar avatar */
  avatarSize: px(s[48]),
  avatarFontSize: "21px",

  /** Encounter panel — expanded-state border (warm tan accent from Figma) */
  encounterPanelExpandBorder: "1.5px solid #C4A882",
} as const;

export const INPUT_FIELD_TEXT_SIZE = "10px";

/** Row height for billing encounter table */
export const BILLING_TABLE_ROW_HEIGHT = 40;

/**  account receivables */
export const ACCOUNT_RECEIVALES_COLORS = {
  patientBar: c.orange[500],
  insuranceBar: c.brandBlue[300],
  duePaymentCardsBg: c.neutrals[550],
};
// revenue graph colors
